import { useEffect, useState, type ReactNode } from "react";

import { getSupabaseClient } from "../providers/supabase/supabase";
import { ATTACHMENTS_BUCKET } from "../providers/commons/attachments";
import { AudioPill } from "./AudioPill";

// LDF: Der Anhang-Speicher ist privat. Links werden bei Bedarf signiert (kurzlebig, nur mit Login).

const SIGNED_SECONDS = 300;

// Pfad aus gespeicherten Angaben ermitteln (neue Einträge: path; alte: öffentliche URL)
export const storagePath = (file: { path?: string | null; src?: string | null }) => {
  if (file.path) return file.path;
  const marker = `/${ATTACHMENTS_BUCKET}/`;
  const src = file.src ?? "";
  const i = src.indexOf(marker);
  return i >= 0 ? decodeURIComponent(src.slice(i + marker.length).split("?")[0]) : null;
};

export const signedUrl = async (path: string) => {
  const { data, error } = await getSupabaseClient().storage.from(ATTACHMENTS_BUCKET).createSignedUrl(path, SIGNED_SECONDS);
  if (error || !data) throw error ?? new Error("Link konnte nicht erzeugt werden");
  return data.signedUrl;
};

// Öffnet eine Datei in neuem Tab. Das Fenster wird sofort geöffnet (Safari blockiert sonst Pop-ups).
export const openStorageFile = async (file: { path?: string | null; src?: string | null }) => {
  const path = storagePath(file);
  if (!path) {
    if (file.src) window.open(file.src, "_blank", "noopener");
    return;
  }
  const win = window.open("", "_blank");
  try {
    const url = await signedUrl(path);
    if (win) win.location.href = url;
    else window.location.href = url;
  } catch {
    win?.close();
    alert("Die Datei konnte nicht geöffnet werden. Bitte erneut anmelden.");
  }
};

export const StorageLink = ({
  file,
  className,
  title,
  children,
}: {
  file: { path?: string | null; src?: string | null };
  className?: string;
  title?: string;
  children: ReactNode;
}) => (
  <a
    href="#"
    title={title}
    className={className}
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      openStorageFile(file);
    }}
  >
    {children}
  </a>
);

/** Sprachnachricht direkt abspielen */
export const StorageAudio = ({ file, title }: { file: { path?: string | null; src?: string | null; type?: string }; title?: string }) => {
  const [failed, setFailed] = useState(false);
  const path = storagePath(file);
  return (
    <div className="flex min-w-0 flex-col gap-1">
      {title && <span className="text-xs text-muted-foreground">🎙️ {title}</span>}
      {/* Signierter Link wird erst beim Antippen geholt */}
      <AudioPill src={path ? () => signedUrl(path) : (file.src ?? "")} onError={() => setFailed(true)} className="w-full max-w-sm" />
      {failed && <span className="text-xs text-muted-foreground">Wiedergabe gerade nicht möglich – bitte kurz warten und erneut abspielen.</span>}
    </div>
  );
};

export const StorageImage = ({
  file,
  alt,
  className,
}: {
  file: { path?: string | null; src?: string | null };
  alt?: string;
  className?: string;
}) => {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const path = storagePath(file);
    if (!path) {
      setUrl(file.src ?? null);
      return;
    }
    let active = true;
    signedUrl(path)
      .then((u) => active && setUrl(u))
      .catch(() => active && setUrl(null));
    return () => {
      active = false;
    };
  }, [file.path, file.src]); // eslint-disable-line react-hooks/exhaustive-deps
  return url ? <img src={url} alt={alt} className={className} /> : <div className={`${className ?? ""} bg-muted`} />;
};
